import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";

import illustration800 from "../assets/login-illustration-800.webp";
import illustration from "../assets/login-illustration.webp";
import logo from "../assets/logo.png";
import { enabledProviders, supabase } from "../supabase";

type Mode = "signin" | "signup" | "forgot";

/** Sign in, sign up, and "forgot password" — one screen, three modes. */
export function Login() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null until Supabase's settings have been read (or if they cannot be).
  const [providers, setProviders] = useState<Record<string, boolean> | null>(null);

  useEffect(() => {
    enabledProviders().then(setProviders);
  }, []);

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
    setMessage(null);
  }

  async function oauth(provider: "google" | "apple") {
    setError(null);
    setMessage(null);
    // The buttons are always shown (as in the design). Only a provider that
    // Supabase has *confirmed* is off says "coming soon"; if we do not know
    // yet, try anyway — Supabase reports a disabled provider itself.
    if (providers !== null && providers[provider] === false) {
      setMessage(`Sign in with ${provider === "apple" ? "Apple" : "Google"} is coming soon.`);
      return;
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      // Back to the same URL, so a QR-code claim link still claims afterwards.
      options: { redirectTo: window.location.href },
    });
    if (error) setError(error.message);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    } else if (mode === "signup") {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.href },
      });
      if (error) setError(error.message);
      else if (!data.session) setMessage("Check your email to confirm your account, then sign in.");
    } else {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      });
      if (error) setError(error.message);
      else setMessage("If there's an account for that address, a reset link is on its way.");
    }
    setBusy(false);
  }

  return (
    <div className="auth">
      <header className="auth-brand">
        <img src={logo} alt="" className="auth-logo" width={64} height={81} />
        <h1>Botherlog</h1>
        <p>Log what's bothering you.</p>
      </header>

      <div className="auth-art" aria-hidden>
        <img
          src={illustration}
          srcSet={`${illustration800} 800w, ${illustration} 1200w`}
          sizes="(max-width: 520px) 100vw, 520px"
          alt=""
          width={1200}
          height={675}
        />
      </div>

      <form className="auth-form" onSubmit={submit}>
        {mode === "forgot" && (
          <p className="auth-hint">Enter your email and we'll send you a link to choose a new password.</p>
        )}

        <Field icon={<MailIcon />}>
          <input
            type="email"
            autoComplete="email"
            required
            placeholder="Email address"
            aria-label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        {mode !== "forgot" && (
          <Field
            icon={<LockIcon />}
            trailing={
              <button
                type="button"
                className="auth-eye"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            }
          >
            <input
              type={showPassword ? "text" : "password"}
              required
              minLength={8}
              placeholder="Password"
              aria-label="Password"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}

        {mode === "signin" && (
          <button type="button" className="auth-link auth-forgot" onClick={() => switchTo("forgot")}>
            Forgot password?
          </button>
        )}

        {error && <p className="auth-error" role="alert">{error}</p>}
        {message && <p className="auth-message" role="status">{message}</p>}

        <button className="auth-primary" disabled={busy}>
          {mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
          <ArrowIcon />
        </button>

        {mode !== "forgot" && (
          <>
            <div className="auth-divider"><span>or continue with</span></div>
            <button type="button" className="auth-social" onClick={() => oauth("google")}>
              <GoogleIcon />
              Continue with Google
            </button>
            <button type="button" className="auth-social" onClick={() => oauth("apple")}>
              <AppleIcon />
              Continue with Apple
            </button>
          </>
        )}

        <p className="auth-switch">
          {mode === "signin" ? (
            <>
              Don't have an account?{" "}
              <button type="button" className="auth-link" onClick={() => switchTo("signup")}>Sign up</button>
            </>
          ) : (
            <>
              {mode === "signup" ? "Already have an account? " : "Remembered it? "}
              <button type="button" className="auth-link" onClick={() => switchTo("signin")}>Sign in</button>
            </>
          )}
        </p>
      </form>
    </div>
  );
}

/** After following a reset link: choose the new password. */
export function SetNewPassword({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) setError(error.message);
    else onDone();
  }

  return (
    <div className="auth">
      <header className="auth-brand">
        <img src={logo} alt="" className="auth-logo" width={64} height={81} />
        <h1>New password</h1>
        <p>Choose a password you haven't used here before.</p>
      </header>
      <form className="auth-form" onSubmit={submit}>
        <Field
          icon={<LockIcon />}
          trailing={
            <button type="button" className="auth-eye" aria-label={show ? "Hide password" : "Show password"}
                    onClick={() => setShow((v) => !v)}>
              {show ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          }
        >
          <input type={show ? "text" : "password"} required minLength={8} autoComplete="new-password"
                 placeholder="New password" aria-label="New password" value={password}
                 onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button className="auth-primary" disabled={busy}>Save password <ArrowIcon /></button>
      </form>
    </div>
  );
}

function Field({ icon, trailing, children }: { icon: ReactNode; trailing?: ReactNode; children: ReactNode }) {
  return (
    <label className="auth-field">
      <span className="auth-field-icon">{icon}</span>
      {children}
      {trailing}
    </label>
  );
}

/* --- Icons (inline, so the page needs no icon font) ----------------------- */

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function MailIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
      <path d="M10.6 5.6A9.7 9.7 0 0 1 12 5.5C18 5.5 21.5 12 21.5 12a17 17 0 0 1-3 3.8M6.6 6.6C3.9 8.4 2.5 12 2.5 12S6 18.5 12 18.5a9.3 9.3 0 0 0 4.4-1.1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" {...stroke} strokeWidth={2.2}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/** Google's "G", per their sign-in branding guidelines. */
function GoogleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M16.4 12.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.8 1.2 1.8 2.6 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8 0 0-2.5-1-2.5-3.8zM14 5.1c.7-.8 1.1-1.9 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" />
    </svg>
  );
}
